/* eslint-disable no-plusplus */
/* eslint-disable no-unused-expressions */
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { estimateClaim, getRAReason } from '@api';
import {
  RespCode, ErrorCode, SessionStorageKey, platformToPolicyTypeMap,
  policyTypeToPlatformMap, PoizonMerchantID, regEmail, regPhoneLogin, InputStatus,
  channelMap, PLATFORM_NAME, PLATFORM_REG, dhgate, ErrorCodeToSpecialText, policyTypeToSpecialPlatformMap, env,
} from '@constants';
import {
  useQuery, useEffectOnce,
  useUrlJump, useBehaviorData,
} from '@hooks';
import { logError } from '@log';
import {
  setCustomerInfo, setPolicyInfo, setClaimInfo, setRAReasonList, setPlatform, setSpecialPlatform,
} from '@store';

// const regOrderId = /^([0-9])+/;

/** 设置 email */
export const onChangeEmail = ({
  event,
  setEmail,
  emailStatus,
  setEmailStatus,
}) => {
  const { value } = event.target;
  setEmail(value.trim());
  if (emailStatus !== InputStatus.Changed) {
    setEmailStatus(InputStatus.Changed);
  }
};

/** 设置 order id */
const onChangeOrderId = ({
  event,
  setOrderId,
  orderStatus,
  setOrderStatus,
}) => {
  const { value } = event.target;
  setOrderId(value.trim());
  if (orderStatus !== InputStatus.Changed) {
    setOrderStatus(InputStatus.Changed);
  }
};

const getRAReasonList = async (dispatch, policyId) => {
  const { code, data } = await getRAReason(policyId);
  if (code === 0) {
    const list = data.map((item) => ({
      ...item,
      value: item.code,
      label: item.name,
    }));
    dispatch(setRAReasonList(list));
  }
};

/** 提交信息 */
export const submit = async ({
  email,
  orderId,
  signature,
  setIsInvalidBarShow = () => { },
  setIsLoading = () => { },
  navigate,
  dispatch, policyType,
  setReportData,
  // platform: oldPlatform,
}) => {
  const { pathname } = window.location || {};
  let platform = (pathname?.match(PLATFORM_REG) || [])[0];// garmentory/treet/gently/poizon...
  if (pathname.indexOf(dhgate) > -1) {
    platform = dhgate;
  }
  try {
    setIsLoading(true);
    setReportData({ loginTime: `${Date.now()}` });
    let orderNumber = orderId;
    // gently 的orderNumber全部转为小写
    if (platform === PLATFORM_NAME.GENTLY) {
      orderNumber = orderId.toLocaleLowerCase();
    }
    dispatch(setPolicyInfo({ email, orderNumber, signature }));
    // 后续皆根据channel缩小后端查询范围
    const channel = channelMap[platform];
    const path = platform === dhgate ? '/gateway/claim-service/api/resolution-center/dhgate/' : undefined;
    const headers = platform === dhgate ? { signature, 'order-id': orderNumber } : {};
    const resp = await estimateClaim({
      email, orderNumber, policyType, channel, path, headers, signature,
    });
    if (!resp) {
      setIsInvalidBarShow({ show: true });
      setIsLoading(false);
      return;
    }
    // const hasEmail = NO_EMAIL_CHANNEL.includes(platform);
    const { code, data } = resp;
    const hasPlatform = platform ? `/${platform}` : '';
    if (code !== RespCode.Success) {
      // eslint-disable-next-line max-len
      if (ErrorCodeToSpecialText.includes(code)) {
        navigate(`${hasPlatform}/error`, { state: { code } });
      } else if (!email && code === ErrorCode.Nothing) {
        navigate(`${hasPlatform}/error`, { state: { code: ErrorCode.Nothing, notShowButton: !email } });
      } else if (code === ErrorCode.Nothing) {
        const msg = 'No Seel covers found for this order. Please double check or contact support for help.';
        setIsInvalidBarShow({ show: true, message: msg });
      } else {
        navigate(`${hasPlatform}/error`, { state: { code: ErrorCode.Wrong, notShowButton: !email } });
      }
    } else {
      setIsInvalidBarShow({ show: false });
      const { policyList = [], redirectToNewRc } = data;
      // 增加rc3.0重定向
      if (redirectToNewRc) {
        window.location.replace(`https://resolution${env === 'prod' ? '' : '-dev'}.seel.com/${platform || ''}?order_number=${orderNumber}${email ? `&email=${encodeURIComponent(email)}` : ''}${signature ? `&signature=${signature}` : ''}`);
        return;
      }
      dispatch(setCustomerInfo(data));
      // 如果理赔类型为ra，获取reasonList
      const RAInfo = policyList.find((v) => v.policyType.indexOf('RA') > -1 || v.policyType.indexOf('WFP') > -1);
      if (RAInfo) {
        getRAReasonList(dispatch, RAInfo.policyId);
      }
      // let platform = channelMap[policyList[0]?.channel];
      for (let i = 0; i < policyList.length; i++) {
        const element = policyList[i].policyType;
        if (Object.keys(policyTypeToSpecialPlatformMap).includes(element)) {
          dispatch(setSpecialPlatform(policyTypeToSpecialPlatformMap[element]));
        }
        if (policyTypeToPlatformMap[element]) {
          platform = policyTypeToPlatformMap[element];
          break;
        }
        // 得物的policyType='SHOPIFY_BP' 为了和shopify区分，这里用merchantID来识别
        if (PoizonMerchantID.includes(policyList[i].merchantId)) {
          platform = 'poizon';
        }
      }
      dispatch(setPlatform(platform));
      // 有理赔记录直接跳转反馈页面
      if (data.claimList?.length > 0) {
        // 因为代码放在不同路径下，需要跳转到对应的平台路径下
        const path = platform ? `${platform}/` : '';
        dispatch(setClaimInfo({ type: '', ...policyList[0] }));
        navigate(`/${path}feedback?claimId=${data.claimList[0]?.claimId}`);
        return;
      }
      useUrlJump({
        policyList, email, navigate, dispatch, setIsInvalidBarShow, platform,
      });
    }
    setIsLoading(false);
  } catch (error) {
    navigate('../error', { state: { code: ErrorCode.Wrong } });
    logError(error);
  }
};

export const useLogin = () => {
  const { query: { email: defaultEmail = '', order_number: defaultOrderId = '', signature } } = useQuery();
  /** email */
  const [email, setEmail] = useState(defaultEmail);
  /** order id */
  const [orderId, setOrderId] = useState(defaultOrderId);
  /** email 输入框状态 */
  const [emailStatus, setEmailStatus] = useState(
    defaultEmail ? InputStatus.Changed : InputStatus.Init,
  );
  /** order id 输入框状态 */
  const [orderStatus, setOrderStatus] = useState(
    defaultOrderId ? InputStatus.Changed : InputStatus.Init,
  );
  /** 是否显示错误提示 */
  const [isInvalidBarShow, setIsInvalidBarShow] = useState({ show: false, message: '' });
  /** 按钮是否 loading */
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { platform } = useSelector((state) => state.policy);
  const setReportData = useBehaviorData();

  /** email 是否合法 */
  const isEmailValid = regEmail.test(email) || regPhoneLogin.test(email);
  /** order id 是否合法 */
  const isOrderIdValid = orderId.length > 0;
  /** 是否显示 email 非法提示 */
  const isEmailShownValid = emailStatus === InputStatus.Init || isEmailValid;
  /** 是否显示 order id 非法提示 */
  const isOrderShownValid = orderStatus === InputStatus.Init || isOrderIdValid;
  const emailLabelText = 'Email Address / Phone Number';
  /** email 非法提示文案 */
  const emailHelperText = email ? `Invalid ${emailLabelText}` : `${emailLabelText} is required`;
  const orderLabelText = `Order Number / ${platform === PLATFORM_NAME.SIDELINESWAP ? 'Swap ID' : 'Cover ID'}`;
  /** order id 非法提示文案 */
  const orderIdHelperText = orderId ? `Invalid ${orderLabelText}` : `${orderLabelText} is required`;

  /** 提交按钮的状态，只有当2个都有效的情况下，才可以提交 */
  const isBtnDisable = !(isEmailValid && isOrderIdValid);

  // 根据platform，确定policyType，在登录的时候传给接口，方便接口查找，目前只支持gently/treet  后续直接根据
  const policyType = platformToPolicyTypeMap[platform];

  useEffectOnce(() => {
    // usablePlatform && dispatch(setPlatform(usablePlatform));
    // 如果 url 上带有参数，自动登录
    const hasLogin = sessionStorage.getItem(SessionStorageKey.HasAutoLogin);
    if (!isBtnDisable && !hasLogin) {
      submit({
        email,
        orderId,
        policyType,
        setReportData,
        platform,
        signature,
        setIsInvalidBarShow,
        setIsLoading,
        navigate,
        dispatch,
      });
      sessionStorage.setItem(SessionStorageKey.HasAutoLogin, 'true');
    }
  });

  return {
    email,
    orderId,
    isBtnDisable,
    emailHelperText: isEmailShownValid ? ' ' : emailHelperText,
    orderIdHelperText: isOrderShownValid ? ' ' : orderIdHelperText,
    orderLabelText,
    isEmailValid: isEmailShownValid,
    isOrderIdValid: isOrderShownValid,
    isInvalidBarShow,
    isLoading,
    emailLabelText,
    onChangeEmail: (event) => onChangeEmail({
      event,
      setEmail,
      emailStatus,
      setEmailStatus,
    }),
    onChangeOrderId: (event) => onChangeOrderId({
      event,
      setOrderId,
      orderStatus,
      setOrderStatus,
    }),
    onClickBtn: () => submit({
      email,
      orderId,
      policyType,
      setReportData,
      platform,
      setIsInvalidBarShow,
      setIsLoading,
      navigate,
      dispatch,
    }),
  };
};
